import { useState } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertCircle, Link2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTenantId } from '@/hooks/useTenantId';
import { toast } from 'sonner';

export default function OpportunityBackfill() {
  const { tenantId } = useTenantId();
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{
    total: number;
    linked: number;
    unmatched: number;
    errors: number;
  } | null>(null);

  const runBackfill = async () => {
    if (!tenantId) {
      toast.error('No tenant ID found');
      return;
    }

    setIsRunning(true);
    setProgress(0);
    setResults(null);

    try {
      // Fetch all opportunities without account_plan_id
      const { data: opportunities, error: fetchError } = await supabase
        .from('opportunities')
        .select('id, company_id')
        .eq('tenant_id', tenantId)
        .is('account_plan_id', null)
        .not('company_id', 'is', null);

      if (fetchError) throw fetchError;

      const total = opportunities?.length || 0;
      let linked = 0;
      let unmatched = 0;
      let errors = 0;

      if (total === 0) {
        setResults({ total: 0, linked: 0, unmatched: 0, errors: 0 });
        setIsRunning(false);
        toast.success('No opportunities need linking');
        return;
      }

      // Process each opportunity
      for (let i = 0; i < opportunities.length; i++) {
        const opp = opportunities[i];
        setProgress(Math.round(((i + 1) / total) * 100));

        try {
          // Find active account plan for this company
          const { data: plans, error: planError } = await supabase
            .from('account_plans')
            .select('id')
            .eq('tenant_id', tenantId)
            .eq('company_id', opp.company_id)
            .in('status', ['planning', 'active'])
            .limit(1)
            .single();

          if (planError || !plans) {
            unmatched++;
            continue;
          }

          // Update opportunity with account_plan_id
          const { error: updateError } = await supabase
            .from('opportunities')
            .update({ account_plan_id: plans.id })
            .eq('id', opp.id);

          if (updateError) {
            errors++;
            console.error(`Failed to update opportunity ${opp.id}:`, updateError);
          } else {
            linked++;
          }
        } catch (err) {
          errors++;
          console.error(`Error processing opportunity ${opp.id}:`, err);
        }
      }

      setResults({ total, linked, unmatched, errors });
      
      if (linked > 0) {
        toast.success(`Successfully linked ${linked} opportunities to account plans`);
      }
    } catch (error) {
      console.error('Backfill error:', error);
      toast.error('Failed to run backfill: ' + (error as Error).message);
    } finally {
      setIsRunning(false);
      setProgress(100);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Opportunity Backfill</h1>
          <p className="text-muted-foreground mt-2">
            Link existing opportunities to account plans based on company matching
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Backfill Existing Opportunities</CardTitle>
            <CardDescription>
              This tool will automatically link opportunities to account plans where the
              company matches. Only opportunities without an existing account plan link will
              be processed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This process will update opportunities that:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Have a company assigned</li>
                  <li>Don't already have an account plan link</li>
                  <li>Have a matching active or planning account plan for that company</li>
                </ul>
              </AlertDescription>
            </Alert>

            {isRunning && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Processing opportunities...</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {results && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardDescription>Total Processed</CardDescription>
                      <CardTitle className="text-3xl">{results.total}</CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardDescription className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Linked
                      </CardDescription>
                      <CardTitle className="text-3xl text-green-600">
                        {results.linked}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardDescription className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-yellow-500" />
                        No Match
                      </CardDescription>
                      <CardTitle className="text-3xl text-yellow-600">
                        {results.unmatched}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardDescription className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        Errors
                      </CardDescription>
                      <CardTitle className="text-3xl text-red-600">
                        {results.errors}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                </div>

                {results.unmatched > 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {results.unmatched} opportunities could not be matched to an account plan.
                      This typically means there is no active account plan for the associated company.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={runBackfill}
                disabled={isRunning}
                size="lg"
              >
                {isRunning ? 'Running...' : 'Run Backfill'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Note:</strong> This is a one-time utility. Once all opportunities are linked,
            new opportunities will automatically be linked when created from account plan pages or
            when you select a company that has an active account plan.
          </AlertDescription>
        </Alert>
      </div>
    </DashboardLayout>
  );
}
